// Styled-components de StyleManager.
// Qué hace: agrupa todos los styled-components (CSS-data) usados por
//   components/StyleManager.tsx — grilla de presets, cards, modales, dropzone,
//   textareas de prompts, etc. Extraído para mantener el componente < 500 LOC.
// Qué exporta: cada styled como `export const`.
// Quién lo usa: components/StyleManager.tsx (único consumidor).
//
// ⚠️ Todo acá va por tokens del tema (theme.colors.*), NUNCA colores hardcodeados.
// Antes el panel estaba cableado a modo claro (cards y modal con `#fff` fijo) mientras
// los textos usaban theme.colors.text: en dark eso daba texto casi blanco sobre fondo
// blanco = ilegible, y los inputs nativos salían oscuros (color-scheme) dentro de un
// modal blanco. Los tints se hacen con sufijo de alpha en hex (ej: `${primary}1A`).

import styled from 'styled-components';

export const Section = styled.section`margin-top: ${({ theme }) => theme.spacing.xl};`;

export const SectionLabel = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  letter-spacing: 0.01em;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

/** Texto de ayuda debajo de un SectionLabel. */
export const Hint = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.45;
  margin: -2px 0 ${({ theme }) => theme.spacing.sm};
`;

export const PresetGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
`;

export const PresetCard = styled.div`
  text-align: left;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surface};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
  transition: border-color 150ms ease, transform 150ms ease, box-shadow 150ms ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
  }

  .preview {
    width: 100%;
    aspect-ratio: 3 / 4;
    background: ${({ theme }) => theme.colors.surfaceHover};
    overflow: hidden;
    display: block;
    img { width: 100%; height: 100%; object-fit: cover; display: block; }
  }
  .body { padding: ${({ theme }) => theme.spacing.md}; }
  h3 {
    font-size: ${({ theme }) => theme.fontSize.md};
    font-weight: ${({ theme }) => theme.fontWeight.semibold};
    margin-bottom: ${({ theme }) => theme.spacing.xs};
    color: ${({ theme }) => theme.colors.text};
  }
  p { font-size: ${({ theme }) => theme.fontSize.sm}; color: ${({ theme }) => theme.colors.textSecondary}; line-height: 1.4; }
`;

export const NewStyleCard = styled.button`
  text-align: center;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  border: 2px dashed ${({ theme }) => theme.colors.borderHover};
  background: ${({ theme }) => theme.colors.background};
  cursor: pointer;
  transition: all 150ms ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  min-height: 220px;
  padding: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-family: inherit;
  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => `${theme.colors.primary}14`};
    color: ${({ theme }) => theme.colors.primary};
  }
  .plus { font-size: 32px; line-height: 1; font-weight: 300; }
  strong { font-size: ${({ theme }) => theme.fontSize.md}; font-weight: ${({ theme }) => theme.fontWeight.semibold}; }
  small { font-size: ${({ theme }) => theme.fontSize.sm}; line-height: 1.4; }
`;

export const DeleteStyleBtn = styled.button`
  position: absolute; top: 8px; right: 8px; width: 26px; height: 26px;
  border-radius: 999px; border: none; background: rgba(15, 23, 42, 0.65);
  color: #fff; font-size: 16px; line-height: 1; cursor: pointer;
  display: flex; align-items: center; justify-content: center; z-index: 2;
  transition: background 150ms ease;
  &:hover { background: ${({ theme }) => theme.colors.error}; }
`;

export const EditStyleBtn = styled.button`
  position: absolute; top: 8px; left: 8px; width: 26px; height: 26px;
  border-radius: 999px; border: none; background: rgba(15, 23, 42, 0.65);
  color: #fff; font-size: 13px; line-height: 1; cursor: pointer;
  display: flex; align-items: center; justify-content: center; z-index: 2;
  transition: background 150ms ease;
  &:hover { background: ${({ theme }) => theme.colors.primary}; }
`;

export const CustomBadge = styled.span`
  display: inline-block; margin-left: 6px; padding: 1px 7px; border-radius: 999px;
  background: ${({ theme }) => `${theme.colors.primary}22`};
  color: ${({ theme }) => theme.colors.primary};
  border: 1px solid ${({ theme }) => `${theme.colors.primary}44`};
  font-size: 10px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold}; vertical-align: middle;
`;

export const VarButton = styled.button`
  padding: 4px 9px; border-radius: 999px;
  border: 1px solid ${({ theme }) => `${theme.colors.primary}44`};
  background: ${({ theme }) => `${theme.colors.primary}14`};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 11px; font-weight: 600;
  font-family: inherit; cursor: pointer; transition: all 120ms ease;
  &:hover { background: ${({ theme }) => `${theme.colors.primary}2A`}; }
`;

/** Campos de texto del panel (antes era el objeto inline `SmallInput`, ciego al tema). */
export const TextInput = styled.input`
  width: 100%;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.borderHover};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  font-size: 14px;
  font-family: inherit;
  transition: border-color 120ms ease, box-shadow 120ms ease;
  &::placeholder { color: ${({ theme }) => theme.colors.textMuted}; }
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => `${theme.colors.primary}22`};
  }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

export const PromptTextarea = styled.textarea`
  width: 100%; padding: ${({ theme }) => theme.spacing.sm} 12px;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.borderHover};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  font-size: 13px; font-family: inherit; line-height: 1.45; resize: vertical; min-height: 120px;
  transition: border-color 120ms ease, box-shadow 120ms ease;
  &::placeholder { color: ${({ theme }) => theme.colors.textMuted}; }
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => `${theme.colors.primary}22`};
  }
`;

export const DropZone = styled.div<{ $hasFile: boolean }>`
  border: 2px dashed ${({ $hasFile, theme }) => ($hasFile ? theme.colors.primary : theme.colors.borderHover)};
  border-radius: ${({ theme }) => theme.borderRadius.lg}; padding: ${({ theme }) => theme.spacing.xl};
  text-align: center;
  background: ${({ $hasFile, theme }) => ($hasFile ? `${theme.colors.primary}14` : theme.colors.background)};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer; transition: all 150ms ease;
  p { font-size: ${({ theme }) => theme.fontSize.sm}; }
  &:hover { border-color: ${({ theme }) => theme.colors.primary}; background: ${({ theme }) => `${theme.colors.primary}14`}; }
`;

export const PreviewImg = styled.img`
  max-width: 240px; max-height: 240px; border-radius: ${({ theme }) => theme.borderRadius.md};
  margin: 0 auto; display: block;
`;

/** Miniatura chica (referencia del estilo, thumbnail del editor). */
export const ThumbImg = styled.img`
  width: 96px; height: 96px; object-fit: cover; flex-shrink: 0;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surfaceHover};
`;

export const HiddenInput = styled.input`display: none;`;

export const Actions = styled.div`
  display: flex; gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.xl};
  padding-top: ${({ theme }) => theme.spacing.md};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

export const ErrorBox = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md}; padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => `${theme.colors.error}14`};
  border: 1px solid ${({ theme }) => `${theme.colors.error}55`};
  color: ${({ theme }) => theme.colors.error};
  border-radius: ${({ theme }) => theme.borderRadius.md}; font-size: ${({ theme }) => theme.fontSize.sm};
`;

export const ModalOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(2, 4, 12, 0.62);
  backdrop-filter: blur(2px);
  display: flex; align-items: center; justify-content: center; z-index: 1000;
  padding: ${({ theme }) => theme.spacing.md};
`;

export const ModalBox = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.xl};
  padding: ${({ theme }) => theme.spacing.xl}; width: 100%; max-width: 460px;
  max-height: 90vh; overflow-y: auto;
  /* Solo el TÍTULO del modal (hijo directo). Si esto fuera \`h2\` a secas pisaría a
     cada SectionLabel anidado — que también es un h2 — y por eso el código viejo
     tenía que parchear con style={{fontSize:14}} en cada label. */
  > h2 {
    font-size: ${({ theme }) => theme.fontSize.xl}; font-weight: ${({ theme }) => theme.fontWeight.bold};
    margin-bottom: ${({ theme }) => theme.spacing.xs}; color: ${({ theme }) => theme.colors.text};
  }
  > p {
    color: ${({ theme }) => theme.colors.textSecondary}; font-size: ${({ theme }) => theme.fontSize.sm};
    line-height: 1.5; margin-bottom: ${({ theme }) => theme.spacing.lg};
  }
  code {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 12px;
    padding: 1px 5px; border-radius: 5px;
    background: ${({ theme }) => theme.colors.surfaceHover};
    color: ${({ theme }) => theme.colors.text};
  }
`;

/** Segmented control del modal (From an image (AI) / Manual). */
export const ModeTabs = styled.div`
  display: inline-flex; gap: 3px; padding: 3px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.surfaceHover};
  border: 1px solid ${({ theme }) => theme.colors.border};
  margin: ${({ theme }) => theme.spacing.xs} 0 ${({ theme }) => theme.spacing.md};
`;

export const ModeTab = styled.button<{ $active: boolean }>`
  padding: 6px 14px; border-radius: 999px; border: none; cursor: pointer;
  font-family: inherit; font-size: 12px; font-weight: 600;
  transition: all 140ms ease;
  background: ${({ $active, theme }) => ($active ? theme.colors.primary : 'transparent')};
  color: ${({ $active, theme }) => ($active ? '#fff' : theme.colors.textSecondary)};
  &:hover { color: ${({ $active, theme }) => ($active ? '#fff' : theme.colors.text)}; }
`;

/** Bloque agrupador (usado por "Test the style") para separarlo del scroll del form. */
export const Panel = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.md};
`;

/** Imagen del sample generado. */
export const SampleImg = styled.img`
  max-width: 280px; width: 100%; display: block;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

export const MutedNote = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 4px 0;
`;

export const SuccessNote = styled.span`
  font-size: 11px; margin-left: 8px;
  color: ${({ theme }) => theme.colors.success};
`;

/** Placeholder cuadrado cuando un estilo no tiene thumbnail. */
export const NoThumb = styled.div`
  width: 64px; height: 80px; border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px dashed ${({ theme }) => theme.colors.borderHover};
  display: grid; place-items: center;
  font-size: 11px; color: ${({ theme }) => theme.colors.textMuted};
`;

/** Thumbnail actual en el modal de edición. */
export const EditThumb = styled.img`
  width: 64px; height: 80px; object-fit: cover;
  border-radius: ${({ theme }) => theme.borderRadius.md};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.surfaceHover};
`;

/** Encabezado de un grupo de variables en el VariablePicker. */
export const GroupLabel = styled.div`
  font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: 4px;
`;

/** Fila horizontal con wrap (inputs + botones). */
export const Row = styled.div`
  display: flex; gap: ${({ theme }) => theme.spacing.sm}; flex-wrap: wrap; align-items: center;
`;
